
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, Mail, Calendar, Settings, Bell } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/useAuth";
import { usePromoCode } from '@/hooks/usePromoCode';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from "@/integrations/supabase/client";
import NavigationBar from "@/components/NavigationBar";
import { useToast } from "@/hooks/use-toast";
import { toast } from 'sonner';

interface Profile {
  id: string;
  user_id: string;
  display_name: string | null;
  vote_notifications_enabled: boolean;
  created_at: string;
  updated_at: string;
}

const Profile = () => {
  const { user, signOut } = useAuth();
  const { toast: useToastNotification } = useToast();
  const { redeemPromoCode, loading: promoLoading } = usePromoCode();
  const { refreshSubscription, subscription } = useSubscription();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [voteNotifications, setVoteNotifications] = useState(true);

  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error loading profile:', error);
          return;
        }

        if (data) {
          setProfile(data);
          setDisplayName(data.display_name || '');
          setVoteNotifications(data.vote_notifications_enabled ?? true);
        } else {
          // Create profile if it doesn't exist
          const { data: newProfile, error: createError } = await supabase
            .from('profiles')
            .insert({
              user_id: user.id,
              display_name: user.user_metadata?.display_name || '',
              vote_notifications_enabled: true
            })
            .select()
            .single();

          if (createError) {
            console.error('Error creating profile:', createError);
          } else if (newProfile) {
            setProfile(newProfile);
            setDisplayName(newProfile.display_name || '');
            setVoteNotifications(newProfile.vote_notifications_enabled ?? true);
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const handleSaveProfile = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const profileData = {
        user_id: user.id,
        display_name: displayName.trim() || null,
        vote_notifications_enabled: voteNotifications,
      };

      const { error } = await supabase
        .from('profiles')
        .upsert(profileData, { onConflict: 'user_id' });

      if (error) {
        throw error;
      }

      useToastNotification({
        title: "Profile updated",
        description: "Your profile has been saved successfully.",
      });

      // Refresh profile data
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setProfile(data);
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      useToastNotification({
        title: "Error",
        description: "Failed to save profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePromoCodeRedeem = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    const result = await redeemPromoCode(promoCode);
    if (result.success) {
      await refreshSubscription();
      setPromoCode('');
      setShowPromoCode(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-background">
        <NavigationBar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">Loading profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-background">
      <NavigationBar />
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Profile Settings</h1>
          <p className="text-muted-foreground">Manage your account information and preferences</p>
        </div>

        <div className="space-y-6">
          {/* Profile Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Profile Information
              </CardTitle>
              <CardDescription>
                Update your display name and personal information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-muted"
                />
                <p className="text-sm text-muted-foreground">
                  Email address cannot be changed
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your display name"
                />
                <p className="text-sm text-muted-foreground">
                  This name will be shown as the host name when you create events
                </p>
              </div>

              <Button 
                onClick={handleSaveProfile} 
                disabled={saving}
                className="w-full"
              >
                {saving ? 'Saving...' : 'Save Profile'}
              </Button>
            </CardContent>
          </Card>

          {/* Notification Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Notification Settings
              </CardTitle>
              <CardDescription>
                Manage how you receive notifications about your events
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between space-x-2">
                <div className="space-y-1">
                  <Label htmlFor="vote-notifications">Vote Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive email notifications when guests vote on your events
                  </p>
                </div>
                <Switch
                  id="vote-notifications"
                  checked={voteNotifications}
                  onCheckedChange={setVoteNotifications}
                />
              </div>
            </CardContent>
          </Card>

          {/* Account Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">User ID</Label>
                  <p className="text-sm text-muted-foreground font-mono">
                    {user?.id}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Member Since</Label>
                  <p className="text-sm text-muted-foreground">
                    {profile?.created_at ? 
                      new Date(profile.created_at).toLocaleDateString() : 
                      'Unknown'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription & Promo Codes */}
          <Card>
            <CardHeader>
              <CardTitle>Subscription & Promo Codes</CardTitle>
              <CardDescription>
                Manage your subscription and redeem promotional codes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 border rounded-lg bg-muted/50">
                <p className="text-sm font-medium">Current Plan</p>
                <p className="text-sm text-muted-foreground">
                  {subscription?.subscription_tier === 'professional' 
                    ? 'Professional (Lifetime)' 
                    : subscription?.subscription_tier || 'Free'}
                </p>
              </div>
              
              {(!subscription?.promo_code_used || subscription?.subscription_tier === 'free') && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Have a promo code?</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPromoCode(!showPromoCode)}
                      className="text-xs"
                    >
                      {showPromoCode ? 'Hide' : 'Enter Code'}
                    </Button>
                  </div>
                  
                  {showPromoCode && (
                    <div className="space-y-3">
                      <Input 
                        type="text" 
                        value={promoCode} 
                        onChange={e => setPromoCode(e.target.value.toUpperCase())} 
                        placeholder="Enter promo code" 
                      />
                      <Button 
                        onClick={handlePromoCodeRedeem} 
                        disabled={promoLoading || !promoCode.trim()}
                        size="sm"
                        className="w-full"
                      >
                        {promoLoading ? 'Redeeming...' : 'Redeem Code'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              {subscription?.promo_code_used && (
                <div className="p-3 border rounded-lg bg-green-50 border-green-200">
                  <p className="text-sm font-medium text-green-800">Promo Code Applied!</p>
                  <p className="text-xs text-green-600">Code: {subscription.promo_code_used}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Account Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Account Actions
              </CardTitle>
              <CardDescription>
                Manage your account settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                variant="destructive" 
                onClick={handleSignOut}
                className="w-full"
              >
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;
